/*
 * FILE: unified_ui/src/stores/wizard.ts
 * PURPOSE: Pinia store for the new-order wizard. Drives the multi-step search flow
 *          through real Core NATS commands via webBridge.
 * OWNS: step index, wizard state, API calls (prepare, select, start, status poll, cancel,
 *        finalize, rating), polling lifecycle.
 * DOCS: docs/product.md (wizard flow), docs/arch_core.md (Core command subjects)
 */
import { defineStore } from 'pinia'
import { call } from '@/lib/botSuiteClient'
import { useOrdersStore } from '@/stores/orders'
import { trackEvent, type TrackEventMeta } from '@/lib/trackEvent'
import { reportEvent } from '@/lib/report'
import {
  CMD,
  type ExpansionCandidate,
  type SearchPrepareResponse,
  type SearchSelectExpansionResponse,
  type SearchStartResponse,
  type SearchStatusResponse,
  type SearchFinalizeResponse,
  type SearchFinalizeRequest,
  type JobProgress,
  type SearchExecutionSnapshot,
} from '@/lib/commands'

export type WizardStepId = 'query' | 'expansion' | 'purpose' | 'confirm' | 'running' | 'done'

export { type ExpansionCandidate }

export const PURPOSE_OPTIONS = [
  '🏦 Для обоснования НМЦК',
  '🧾 Подбор для заказчика (перепродажа)',
  '🤫 Для собственных нужд',
] as const

/** Magic token — entering this as the query fast-tracks the wizard. */
export const FAST_TEST_TOKEN = '!test'

export const STEP_ORDER: WizardStepId[] = [
  'query', 'expansion', 'purpose', 'confirm', 'running', 'done',
]

/** Human-readable Russian labels for wizard steps — used in ChatLayer path events. */
export const STEP_LABELS: Record<WizardStepId, string> = {
  query: 'Что ищете?',
  expansion: 'Уточните запрос',
  purpose: 'Зачем ищете?',
  confirm: 'Проверьте детали',
  running: 'Поиск идёт',
  done: 'Готово',
}

/**
 * Guards the once-per-run `order.ready` observe event. Keyed by runId so a
 * repeated status poll can never double-report; a new run (new runId) fires
 * again naturally — i.e. the guard is implicitly reset when the run changes.
 */
let observeReadyFiredRunId: string | null = null

type State = {
  step: WizardStepId
  history: WizardStepId[]
  visited: WizardStepId[]
  rawQuery: string
  candidates: ExpansionCandidate[]
  selectedIndex: number | null
  purpose: string
  runProgress: number
  searchProgress: JobProgress | null
  lastOrderId: string | null
  seedQuery: string
  /** Active search runId for polling. */
  runId: string | null
  /** Error message to display in the UI. */
  error: string | null
  /** Whether a rating request is expected after finalization. */
  shouldAskForRating: boolean
  /** Desired expansion candidate count for the next prepare. Configurable per
   *  order entry (a high-value product may ask for more). Omitted/0 → parser
   *  legacy default (4). Set before submitQuery(). */
  expansionLimit: number | null
}

export const useWizardStore = defineStore('wizard', {
  state: (): State => ({
    step: 'query' as WizardStepId,
    history: [],
    visited: ['query'],
    rawQuery: '',
    candidates: [],
    selectedIndex: null,
    purpose: '',
    runProgress: 0,
    searchProgress: null,
    lastOrderId: null,
    seedQuery: '',
    runId: null,
    error: null,
    shouldAskForRating: false,
    // New-order wizard default: ask the parser for 6 candidate products on
    // step 1 (vs the legacy 4). Override per order via setExpansionLimit().
    expansionLimit: 6,
  }),
  getters: {
    progress: (s) => {
      const i = STEP_ORDER.indexOf(s.step)
      return Math.round(((i + 1) / STEP_ORDER.length) * 100)
    },
    canGoBack: (s) => s.history.length > 0 && s.step !== 'running',
    selectedCandidate: (s): ExpansionCandidate | null => {
      if (s.selectedIndex === null) return null
      if (s.selectedIndex === 0) return null // 0 = "use raw query as-is"
      return s.candidates[s.selectedIndex - 1] ?? null
    },
    /** The resolved query string used for search start and finalize. */
    finalQuery: (s): string => {
      const idx = s.selectedIndex
      const c = (idx !== null && idx > 0) ? (s.candidates[idx - 1] ?? null) : null
      if (c) return `${c.category} ${c.product}`
      return s.rawQuery
    },
  },

  actions: {
    // ── Lifecycle ────────────────────────────────────────────

    seed(q: string) {
      this.seedQuery = q
      this.rawQuery = q
    },

    /**
     * Set the desired expansion candidate count for the NEXT prepare
     * (configurable per order entry / product). Set to 0 or null to fall back
     * to the parser legacy default (4).
     */
    setExpansionLimit(limit: number | null) {
      this.expansionLimit = limit && limit > 0 ? Math.trunc(limit) : null
    },

    start() {
      this._stopPolling()
      this.step = 'query'
      this.history = []
      this.visited = ['query']
      this.rawQuery = this.seedQuery || ''
      this.candidates = []
      this.selectedIndex = null
      this.purpose = ''
      this.runProgress = 0
      this.lastOrderId = null
      this.runId = null
      this.error = null
      this.shouldAskForRating = false
      this._trackStep('started', 'query', 'Мастер начат')
    },

    /** Seed the query and reset to step 1 for a repeat order. */
    repeat(query: string) {
      this._stopPolling()
      this.seed(query)
      this.step = 'query'
      this.history = ['query']
      this.visited = ['query']
      this.rawQuery = query
      this.candidates = []
      this.selectedIndex = null
      this.purpose = ''
      this.runProgress = 0
      this.lastOrderId = null
      this.runId = null
      this.error = null
      this.shouldAskForRating = false
      this._trackStep('repeated', 'query', `Повтор: ${query}`, { context: { query } })
    },

    // ── Step 1: Query → Prepare ─────────────────────────────

    async submitQuery() {
      const q = this.rawQuery.trim()
      if (!q) return

      this.error = null
      const result = await call<{ rawQuery: string; limit?: number }, SearchPrepareResponse>(
        CMD.searchPrepare,
        {
          rawQuery: q,
          // Only send a limit when one was configured (default 6 for this
          // wizard) — omitting it would fall back to the parser legacy 4.
          ...(this.expansionLimit && this.expansionLimit > 0 ? { limit: this.expansionLimit } : {}),
        },
      )

      if (!result.success) {
        this.error = result.error
        this._trackStep('submitted', 'query', `Запрос отклонён: ${q}`, {
          context: { query: q, outcome: 'error', error: result.error },
        })
        return
      }

      const data = result.data
      if (!data) {
        this.error = 'empty_response'
        this._trackStep('submitted', 'query', `Запрос отклонён: ${q}`, {
          context: { query: q, outcome: 'error', error: 'empty_response' },
        })
        return
      }

      switch (data.kind) {
        case 'expansion_options_available': {
          const candidates = data.expansion?.candidates ?? []
          this.candidates = candidates
          this._trackStep('submitted', 'query', `Запрос: ${q} (вариантов: ${candidates.length})`, {
            context: { query: q, outcome: 'expansion_ready', candidatesCount: candidates.length },
          })
          this._go('expansion')
          break
        }
        case 'validation_failure':
          this.error = 'Запрос не прошёл валидацию. Уточните формулировку.'
          this._trackStep('submitted', 'query', `Запрос отклонён: ${q}`, {
            context: { query: q, outcome: 'validation_failure' },
          })
          break
        case 'pre_query_denial':
          this.error = 'По данному запросу поиск невозможен. Попробуйте другую формулировку.'
          this._trackStep('submitted', 'query', `Запрос невозможен: ${q}`, {
            context: { query: q, outcome: 'denied' },
          })
          break
        case 'expansion_unavailable':
          this.error = 'Не удалось подобрать варианты. Попробуйте уточнить запрос.'
          this._trackStep('submitted', 'query', `Варианты не найдены: ${q}`, {
            context: { query: q, outcome: 'expansion_unavailable' },
          })
          break
        default:
          this.error = 'unexpected_response'
          this._trackStep('submitted', 'query', `Запрос отклонён: ${q}`, {
            context: { query: q, outcome: 'error', error: 'unexpected_response' },
          })
      }
    },

    // ── Step 2: Select Expansion ────────────────────────────

    async selectCandidate(index: number) {
      this.selectedIndex = index
      this.error = null

      const allCandidates = this.candidates

      // Backend uses 1-based indexing: 1 = "use original query", 2 = first candidate, etc.
      const backendIndex = index + 1

      const result = await call<{ originalQuery: string; candidates: ExpansionCandidate[]; selectionIndex: number }, SearchSelectExpansionResponse>(
        CMD.searchSelectExpansion,
        {
          originalQuery: this.rawQuery,
          candidates: allCandidates,
          selectionIndex: backendIndex,
        },
      )

      if (!result.success) {
        this.error = result.error
        this._trackStep('selected', 'expansion', 'Вариант не выбран', {
          context: { selectionIndex: backendIndex, outcome: 'error', error: result.error },
        })
        return
      }

      const data = result.data
      if (!data) {
        this.error = 'empty_response'
        this._trackStep('selected', 'expansion', 'Вариант не выбран', {
          context: { selectionIndex: backendIndex, outcome: 'error', error: 'empty_response' },
        })
        return
      }

      // Human-readable selection for the timeline: "как есть: <query>" for the
      // original-query option, otherwise "<category> <product>" of the candidate.
      const selectedText =
        index === 0
          ? `как есть: ${this.rawQuery}`
          : `${allCandidates[index - 1]?.category ?? ''} ${allCandidates[index - 1]?.product ?? ''}`.trim() || this.rawQuery

      switch (data.kind) {
        case 'ready_for_why':
          this._trackStep('selected', 'expansion', `Вариант: ${selectedText}`, {
            context: { selectionIndex: backendIndex, selectedText, outcome: 'ready' },
          })
          this._go('purpose')
          break
        case 'invalid_selection':
          this.error = 'Выбранный вариант недоступен. Попробуйте другой.'
          this._trackStep('selected', 'expansion', `Вариант отклонён: ${selectedText}`, {
            context: { selectionIndex: backendIndex, selectedText, outcome: 'invalid' },
          })
          break
        case 'confirmation_required':
          // The server wants additional confirmation; for now treat as ready_for_why.
          this._trackStep('selected', 'expansion', `Вариант: ${selectedText}`, {
            context: { selectionIndex: backendIndex, selectedText, outcome: 'confirmation' },
          })
          this._go('purpose')
          break
        default:
          this.error = 'unexpected_response'
          this._trackStep('selected', 'expansion', 'Вариант не выбран', {
            context: { selectionIndex: backendIndex, selectedText, outcome: 'error' },
          })
      }
    },

    // ── Step 3: Purpose ─────────────────────────────────────

    selectPurpose(p: string) {
      this.purpose = p
      this.error = null
      this._trackStep('selected', 'purpose', `Цель: ${p}`, { context: { purpose: p } })
      this._go('confirm')
    },

    // ── Fast test flow ────────────────────────────────────
    // Enter "!test" as the query to skip the wizard and fire a
    // real search with canned test data, exercising the full
    // backend pipeline (parser → lead → generation).

    async fastTest() {
      this.rawQuery = 'Сервер HPE ProLiant DL380 Gen11 2x Xeon Gold 32GB DDR5'
      this.purpose = '🏦 Для обоснования НМЦК'
      this.selectedIndex = 0 // "use raw query as-is"
      this.error = null
      await this.startRun()
    },

    // ── Step 4: Confirm → Start Run ─────────────────────────

    async startRun() {
      this.error = null

      const result = await call<{
        query: string
        why?: string
        strict: boolean
        requirePrices: boolean
        selectedExpansion?: ExpansionCandidate
      }, SearchStartResponse>(
        CMD.searchStart,
        {
          query: this.finalQuery,
          why: this.purpose,
          strict: false,
          requirePrices: true,
          selectedExpansion: this.selectedCandidate ?? undefined,
        },
      )

      if (!result.success) {
        this.error = result.error
        return
      }

      const data = result.data
      if (!data?.success) {
        this.error = data?.error ?? 'start_failed'
        return
      }
      if (!data.runId) {
        this.error = data?.error ?? 'start_missing_run_id'
        return
      }

      this.runId = data.runId
      this.lastOrderId = data.runId
      this._go('running')
      this.runProgress = 0

      // Optimistic insert — show the running order in the orders list immediately
      const ordersStore = useOrdersStore()
      ordersStore.prepend({
        id: this.runId,
        title: this.finalQuery,
        purpose: this.purpose || null,
        createdAt: new Date().toISOString(),
        status: 'in_progress',
        hasPdf: false,
      })

      // Observe: the order row was born (search started successfully).
      reportEvent({
        event: 'order.draft_created',
        text: 'Заказ создан',
        data: { runId: this.runId, query: this.finalQuery, expansion: this.selectedCandidate ?? null },
      })

      this._pollStatus()
    },

    // ── Public actions: fetchStatus / cancelRunById ─────────
    // Used by the unified JobStatePanel for both the wizard running step
    // and the order detail view. When searchId !== wizard's own runId,
    // they operate without touching wizard UI state.

    async fetchStatus(searchId: string): Promise<SearchExecutionSnapshot | null> {
      if (!searchId) return null
      const result = await call<{ searchId: string }, SearchStatusResponse>(
        CMD.searchStatus,
        { searchId },
      )
      if (!result.success || !result.data) {
        if (searchId === this.runId) {
          this.error = result.success ? 'empty_response' : result.error
        }
        return null
      }
      const snap = result.data
      console.log('[wizard] fetchStatus searchId=', searchId, 'runId=', this.runId, 'status=', snap.status, 'progress=', snap.progress)
      if (searchId === this.runId) {
        const p = snap.progress
        if (p && (p.total != null || p.processed > 0 || p.currentSource != null)) {
          this.searchProgress = p
          if (p.total && p.total > 0) {
            this.runProgress = Math.min(98, Math.round((p.processed / p.total) * 100))
          } else if (p.processed > 0) {
            this.runProgress = Math.min(90, p.processed)
          } else {
            this.runProgress = 0
          }
        }
      }
      return snap
    },

    async cancelRunById(searchId: string): Promise<boolean> {
      if (!searchId) return false
      if (searchId === this.runId) {
        this._stopPolling()
      }

      // Optimistic update: mark the order as cancelled in the local list
      // immediately so the UI feels instant, even before the server replies.
      const ordersStore = useOrdersStore()

      // Mark as cancelling so the orders poll won't overwrite
      // our optimistic status update while the cancel is in-flight.
      ordersStore.markCancelling(searchId)

      const existing = ordersStore.findById(searchId)
      if (existing && existing.status === 'in_progress') {
        existing.status = 'cancelled'
        existing.progress = null
      }

      const result = await call<{ searchId: string }, SearchStatusResponse>(
        CMD.searchCancel,
        { searchId },
      )

      if (!result.success) {
        // Revert optimistic update on failure.
        if (existing) {
          existing.status = 'in_progress'
        }
        return false
      }

      // Refresh orders in the background to get the authoritative state.
      void ordersStore.refresh(true)

      // If this was the wizard's own run, reset the wizard state so a fresh
      // wizard can start immediately. Without this, re-entering /new-order
      // stays pinned on the (now cancelled) running step until a full page
      // refresh — the user can never reach the query step again.
      if (searchId === this.runId) {
        this._reset()
      }
      return true
    },

    // ── Polling ─────────────────────────────────────────────

    _pollAbortController: null as AbortController | null,

    _pollStatus() {
      // Cancel any existing poll.
      this._stopPolling()

      const ac = new AbortController()
      this._pollAbortController = ac

      const POLL_INTERVAL_MS = 1500
      const MAX_POLLS = 200 // 5 minutes max

      let polls = 0
      const tick = async () => {
        if (ac.signal.aborted) return
        polls++

        const result = await call<{ searchId?: string }, SearchStatusResponse>(
          CMD.searchStatus,
          { searchId: this.runId ?? undefined },
        )

        if (ac.signal.aborted) return

        if (!result.success) {
          this.error = result.error
          this.runProgress = 0
          this._stopPolling()
          return
        }

        const snap = result.data
        if (!snap) return

        const status = snap.status
        if (status === 'completed' || status === 'artifacts_ready') {
          this.runProgress = 100
          this.runId = snap.runId ?? this.runId
          // Observe: order reached a ready state — exactly once per run.
          if (observeReadyFiredRunId !== this.runId) {
            observeReadyFiredRunId = this.runId
            reportEvent({
              event: 'order.ready',
              text: 'Заказ готов',
              data: { runId: this.runId, query: this.finalQuery },
            })
          }
          this._stopPolling()
          await this._finalizeAndDone(snap)
          return
        }

        if (
          status === 'failed' || status === 'timed_out' ||
          status === 'cancelled' || status === 'cancel_error' ||
          status === 'aborted'
        ) {
          this.error = snap.error ?? `search_${status}`
          this.runProgress = 0
          this._stopPolling()
          return
        }

        // Estimate progress. Prefer real progress from parser when available.
        const p = snap.progress
        if (p && (p.total != null || p.processed > 0 || p.currentSource != null)) {
          this.searchProgress = p
          if (p.total && p.total > 0) {
            this.runProgress = Math.min(98, Math.round((p.processed / p.total) * 100))
          } else if (p.processed > 0) {
            this.runProgress = Math.min(90, p.processed)
          } else {
            this.runProgress = 0
          }
        }

        if (polls >= MAX_POLLS) {
          this.error = 'timeout'
          this._stopPolling()
          return
        }

        setTimeout(tick, POLL_INTERVAL_MS)
      }

      setTimeout(tick, POLL_INTERVAL_MS)
    },

    _stopPolling() {
      if (this._pollAbortController) {
        this._pollAbortController.abort()
        this._pollAbortController = null
      }
    },

    // ── Step 6: Done → Finalize + Wait for PDF + Refresh ─────

    async _finalizeAndDone(snap: SearchStatusResponse) {
      this.error = null

      // Pass artifact IDs from the status snapshot so the backend can link them
      const result = await call<SearchFinalizeRequest, SearchFinalizeResponse>(
        CMD.searchFinalize,
        {
          query: this.finalQuery,
          why: this.purpose,
          csvFileId: snap.csvFileId ?? undefined,
          pdfFileId: snap.pdfFileId ?? undefined,
        },
      )

      if (result.success && result.data?.success) {
        this.shouldAskForRating = result.data.shouldAskForRating ?? false
        this.lastOrderId = result.data.runId ?? this.lastOrderId
      }

      // Refresh orders and poll until the PDF is actually ready
      const ordersStore = useOrdersStore()
      const MAX_WAIT_MS = 12000
      const POLL_MS = 800
      const started = Date.now()

      while (Date.now() - started < MAX_WAIT_MS) {
        await ordersStore.refresh()
        const o = this.lastOrderId ? ordersStore.findById(this.lastOrderId) : null
        if (o?.hasPdf) break
        await new Promise(r => setTimeout(r, POLL_MS))
      }

      this._go('done')
    },

    // ── Resume an existing order in the running step ───────
    // Used when the user clicks an active order in the list. The running screen
    // is the single screen for progress; we bring the existing runId into the
    // wizard and start polling.
    async resume(searchId: string) {
      this._stopPolling()
      this._reset()
      this.runId = searchId
      this.lastOrderId = searchId
      this.step = 'running'
      this.runProgress = 0
      this.searchProgress = null
      this.error = null
      this.visited = ['query', 'running']
      this.history = []

      // Pull in the order title/purpose so the done screen can show them when it completes.
      const ordersStore = useOrdersStore()
      await ordersStore.load()
      const order = ordersStore.findById(searchId)
      if (order) {
        this.rawQuery = order.title
        this.purpose = order.purpose || ''
      }

      this._trackStep('resumed', 'running', `Возобновлён: ${order?.title ?? searchId}`, {
        context: { runId: searchId, query: order?.title ?? null },
      })

      this._pollStatus()
    },

    // ── Cancel (wizard's own runId) ─────────────────────────

    async cancelRun() {
      if (this.runId) {
        await this.cancelRunById(this.runId)
      } else {
        this._stopPolling()
      }
      this.runProgress = 0
      this.error = null
      this._reset()
    },

    _reset() {
      this.step = 'query'
      this.history = []
      this.visited = ['query']
      this.rawQuery = ''
      this.candidates = []
      this.selectedIndex = null
      this.purpose = ''
      this.runId = null
      this.searchProgress = null
      this.shouldAskForRating = false
    },

    // ── Rating ──────────────────────────────────────────────

    async submitRating(rate: number, reason?: string) {
      if (rate < 1 || rate > 3) return
      await call<{ rate: number; productQuery?: string; why?: string }, unknown>(
        CMD.ratingSubmit,
        {
          rate,
          productQuery: this.finalQuery,
          // Bad-rate reason wins over the wizard purpose when present —
          // the reason must never be dropped for lack of a phone number.
          why: reason ?? this.purpose,
        },
      )
    },

    // ── Navigation ──────────────────────────────────────────

    back() {
      this._stopPolling()
      const prev = this.history.pop()
      if (prev) {
        this.step = prev
        if (!this.visited.includes(prev)) this.visited.push(prev)
        this._trackStep('back', prev, `Назад: ${STEP_LABELS[prev]}`, {
          context: { query: this.rawQuery },
        })
      }
      this.error = null
    },

    jumpTo(step: WizardStepId) {
      if (this.visited.includes(step) && step !== this.step) {
        if (step === 'running' || step === 'done') return
        this.step = step
        this._trackStep('jump', step, `Переход: ${STEP_LABELS[step]}`, {
          context: { query: this.rawQuery },
        })
        this.error = null
      }
    },

    _go(next: WizardStepId) {
      const currentIdx = STEP_ORDER.indexOf(this.step)
      const nextIdx = STEP_ORDER.indexOf(next)
      if (nextIdx > currentIdx) {
        this.history = this.history.filter(
          (s) => STEP_ORDER.indexOf(s) < currentIdx,
        )
      }
      if (this.history[this.history.length - 1] !== this.step) {
        this.history.push(this.step)
      }
      this.step = next
      if (!this.visited.includes(next)) this.visited.push(next)
      this.error = null
      this._trackStep('entered', next, `${STEP_LABELS[next]} (${STEP_ORDER.indexOf(next) + 1}/${STEP_ORDER.length})`, {
        context: this._stepContext(next),
      })
    },

    // ── Path observability ───────────────────────────────────
    // Every step transition / user choice is reported to ChatLayer as one
    // chronological wizard.step event (see lib/trackEvent.ts). The detail is
    // the human line in the room timeline; meta carries step, action, position
    // and the exact user content so the whole path is traceable. Fire-and-forget.

    /** Context snapshot for the step the user is entering. */
    _stepContext(step: WizardStepId): TrackEventMeta {
      switch (step) {
        case 'expansion':
          return { candidatesCount: this.candidates.length }
        case 'purpose':
          return { selectionIndex: this.selectedIndex }
        case 'confirm':
          return { query: this.finalQuery, purpose: this.purpose }
        case 'running':
          return { runId: this.runId ?? undefined, query: this.finalQuery }
        case 'done':
          return { runId: this.lastOrderId ?? undefined, query: this.finalQuery }
        default:
          return {}
      }
    },

    _trackStep(action: string, step: WizardStepId, detail: string, extra?: TrackEventMeta) {
      trackEvent('wizard-step', detail, {
        step,
        action,
        stepIndex: STEP_ORDER.indexOf(step) + 1,
        totalSteps: STEP_ORDER.length,
        ...(extra ?? {}),
      })
    },
  },
})
